/* @jsx h */
import { defineComponent, h } from "vue";
import type { RestaurantProfileData } from "../types";
import { RichTextEditor } from "@lemmonhq/uikit/rich-text-editor";

export const BusinessInfoSection = defineComponent<{
  data: RestaurantProfileData;
  onUpdate: <K extends keyof RestaurantProfileData>(field: K, value: RestaurantProfileData[K]) => void;
}>({
  name: "BusinessInfoSection",
  props: ["data", "onUpdate"],
  setup(props) {
    return () => (
      <div class="bg-base-100 l-box-shadow  rounded-xl p-6 shadow-sm border ">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <span class="material-symbols-outlined text-primary text-xl">business</span>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-accent">Business Information</h3>
            <p class="text-sm text-base-content/60">Core details about your restaurant</p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-base-content/80 mb-2">Restaurant Name *</label>
            <input
              type="text"
              value={props.data.name}
              onInput={(e) => props.onUpdate("name", (e.target as HTMLInputElement).value)}
              class="input input-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
              placeholder="e.g., The Golden Spoon"
              required
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-base-content/80 mb-2">URL Slug *</label>
            <div class="relative flex items-center">
              <span class="text-base-content/40 text-sm">eat.thelemmon.ch/</span>
              <input
                type="text"
                value={props.data.slug}
                disabled
                onInput={(e) => props.onUpdate("slug", (e.target as HTMLInputElement).value)}
                class="input input-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
                placeholder="golden-spoon"
                required
              />
            </div>
            <p class="text-xs text-base-content/50 mt-1">Used in your restaurant's public URL</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-base-content/80 mb-2">Company Registration *</label>
            <input
              type="text"
              value={props.data.company_registration}
              onInput={(e) => props.onUpdate("company_registration", (e.target as HTMLInputElement).value)}
              class="input input-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
              placeholder="CHE-123.456.789"
              required
            />
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-base-content/80 mb-2">Business Address *</label>
            <textarea
              value={props.data.address}
              onInput={(e) => props.onUpdate("address", (e.target as HTMLTextAreaElement).value)}
              class="textarea textarea-bordered w-full h-24 bg-base-200 focus:bg-base-100 transition-colors resize-none"
              placeholder="Street address, city, postal code"
              required
            />
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-base-content/80 mb-2">About & Description</label>
            <RichTextEditor
              value={props.data.description}
              onChange={(html) => props.onUpdate("description", html)}
            />
            <p class="text-xs text-base-content/50 mt-1">
              Appears on ordering surfaces and helps guests understand your vibe.
            </p>
          </div>

          <div class="md:col-span-2">
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-base-content/80">Geolocation (optional)</label>
              <span class="text-xs text-base-content/60">Improves courier and pickup accuracy</span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label class="block text-xs text-base-content/60 mb-1">Latitude</label>
                <div class="relative">
                  <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg">
                    my_location
                  </span>
                  <input
                    type="number"
                    inputmode="decimal"
                    step="0.000001"
                    value={props.data.geolocation?.lat ?? ""}
                    onInput={(e) => {
                      const value = (e.target as HTMLInputElement).value;
                      const parsed = value === "" ? undefined : Number(value);
                      props.onUpdate("geolocation", {
                        ...props.data.geolocation,
                        lat: parsed === undefined || Number.isNaN(parsed) ? undefined : parsed,
                      });
                    }}
                    class="input input-bordered w-full bg-base-200 focus:bg-base-100 transition-colors pl-10"
                    placeholder="46.5197"
                  />
                </div>
              </div>
              <div>
                <label class="block text-xs text-base-content/60 mb-1">Longitude</label>
                <div class="relative">
                  <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg">
                    explore
                  </span>
                  <input
                    type="number"
                    inputmode="decimal"
                    step="0.000001"
                    value={props.data.geolocation?.lng ?? ""}
                    onInput={(e) => {
                      const value = (e.target as HTMLInputElement).value;
                      const parsed = value === "" ? undefined : Number(value);
                      props.onUpdate("geolocation", {
                        ...props.data.geolocation,
                        lng: parsed === undefined || Number.isNaN(parsed) ? undefined : parsed,
                      });
                    }}
                    class="input input-bordered w-full bg-base-200 focus:bg-base-100 transition-colors pl-10"
                    placeholder="6.6323"
                  />
                </div>
              </div>
              <div>
                <label class="block text-xs text-base-content/60 mb-1">Map Label</label>
                <input
                  type="text"
                  value={props.data.geolocation?.address ?? ""}
                  onInput={(e) =>
                    props.onUpdate("geolocation", {
                      ...props.data.geolocation,
                      address: (e.target as HTMLInputElement).value,
                    })
                  }
                  class="input input-bordered w-full bg-base-200 focus:bg-base-100 transition-colors"
                  placeholder="Entrance or pickup note"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
});
